import type { Vector3 } from 'three/webgpu';
import { Pane } from 'tweakpane';
import { MIN_GAP } from '../../engine/caves/gap';
import { defaultSurfaceGravity, G0 } from '../../engine/universe/HollowDef';
import { type BuiltUniverse, buildUniverse } from './build';
import { BIOMES, PLANET_TYPES, PRESETS, SKIES, SPECIES } from './catalog';
import { PREFAB_IDS, PREFABS, type PrefabId } from './prefabs';
import { type Drag, type Drawn, drawUniverse, drawWorldMap, formatDistance } from './preview';
import {
  applyPreset,
  BIOME_IDS,
  type BiomeId,
  biomeSlot,
  CAVE_LENGTH,
  connect,
  encodeUniverse,
  loadUniverse,
  MAX_BIOMES,
  MAX_WORLDS,
  MOTES_IDS,
  neighbours,
  nextWorld,
  PLANET_TYPE_IDS,
  type PlanetTypeId,
  PRESET_IDS,
  type PresetId,
  RANGES,
  removeWorld,
  SKY_IDS,
  type SkyId,
  SPECIES_IDS,
  saveUniverse,
  skyDefaults,
  typeDefaults,
  type UniverseSpec,
  WHERE_IDS,
  type WorldSpec,
} from './spec';

/*
 * The custom-world editor, opened from the menu: the worlds of your universe in a list, every
 * setting of the selected world in a panel (its preset, planet type, biomes, sky and more), a
 * rough map of it, and the universe seen from outside, where dragging one world onto another
 * joins them by a cave (so any world can be a hub). Changes are saved in this browser as you
 * go; "Generate & play" loads them.
 */

type Folder = ReturnType<Pane['addFolder']>;
type Binding = ReturnType<Pane['addBinding']>;

const WHERE_LABELS: Record<(typeof WHERE_IDS)[number], string> = {
  anywhere: 'Anywhere',
  low: 'Low ground',
  high: 'High ground',
  poles: 'Near the poles',
  equator: 'Near the equator',
};
const MOTES_LABELS: Record<(typeof MOTES_IDS)[number], string> = {
  none: 'Nothing',
  fireflies: 'Fireflies (at night)',
  spores: 'Glowing spores',
  embers: 'Rising embers',
};

let editor: Editor | null = null;

/** Open the editor (built the first time). */
export function openCustomEditor(): void {
  editor ??= new Editor();
  editor.open();
  // For scripted tests: where the universe view drew each world, and what's being edited.
  (window as unknown as { __customEditor?: Editor }).__customEditor = editor;
}

class Editor {
  private readonly root: HTMLElement;
  u: UniverseSpec = loadUniverse();
  private selected = 0;
  private pane: Pane | null = null;
  /** Rows shown only when another setting allows them. */
  private readonly when: { row: Binding | Folder; show: (w: WorldSpec) => boolean }[] = [];
  private built: BuiltUniverse | null = null;
  drawn: Drawn[] = [];
  private drag: Drag | null = null;
  private dragMoved = false;
  private previewTimer = 0;
  private saveTimer = 0;
  /** Bumped by every new preview, so an older map still being drawn stops. */
  private previewEpoch = 0;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'custom-editor';
    this.root.className = 'hidden';
    this.root.innerHTML = `
      <div class="ce-panel" role="dialog" aria-label="Custom worlds">
        <div class="ce-head">
          <div class="ce-title">Custom worlds</div>
          <label class="ce-uname">Universe <input type="text" maxlength="60" /></label>
          <select class="ce-prefab" title="Replace these worlds with a ready-made universe"></select>
          <span class="ce-grow"></span>
          <button type="button" class="ce-close" title="Close (Esc)">Close</button>
        </div>
        <div class="ce-body">
          <div class="ce-list">
            <div class="ce-worlds"></div>
            <button type="button" class="ce-add">+ Add world</button>
            <p class="ce-help">A new world opens into a cave from the world you have selected. In the universe view on the right, drag any world onto another to join the two by a cave instead: put three or more round one world and it's a hub. Where caves open is found for you, on dry, gentle ground at both ends.</p>
          </div>
          <div class="ce-pane"></div>
          <div class="ce-side">
            <div class="ce-cap">Map of <b class="ce-map-name"></b> <span class="dim">(rough; dots are cave mouths)</span></div>
            <canvas class="ce-map" width="384" height="192"></canvas>
            <div class="ce-stats dim"></div>
            <div class="ce-cap">The universe <span class="dim">(click a world to edit it; drag it onto another to join them)</span></div>
            <canvas class="ce-universe" width="384" height="300"></canvas>
            <div class="ce-notes dim"></div>
          </div>
        </div>
        <div class="ce-foot">
          <button type="button" class="ce-remove">Remove world</button>
          <span class="ce-grow"></span>
          <span class="ce-status dim"></span>
          <button type="button" class="ce-share">Copy share link</button>
          <button type="button" class="ce-play">Generate &amp; play</button>
        </div>
      </div>`;
    document.body.appendChild(this.root);
    // Keys typed here are the editor's: not the game's (H hides the interface, ` the debug panel…).
    this.root.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') this.close();
      e.stopPropagation();
    });
    this.root.addEventListener('keyup', (e) => e.stopPropagation());
    this.root.addEventListener('click', (e) => {
      e.stopPropagation();
      if (e.target === this.root) this.close();
    });
    const nameInput = this.q<HTMLInputElement>('.ce-uname input');
    nameInput.addEventListener('input', () => {
      this.u.name = nameInput.value.slice(0, 60) || 'My worlds';
      this.changed(false);
    });
    this.wirePrefabs();
    this.q('.ce-close').addEventListener('click', () => this.close());
    this.q('.ce-add').addEventListener('click', () => this.addWorld());
    this.q('.ce-remove').addEventListener('click', () => {
      this.u = removeWorld(this.u, this.selected);
      this.selected = Math.min(this.selected, this.u.worlds.length - 1);
      this.rebuild();
    });
    this.q('.ce-share').addEventListener('click', () => void this.share());
    this.q('.ce-play').addEventListener('click', () => this.play());
    this.wireUniverseView();
  }

  open(): void {
    this.u = loadUniverse();
    this.selected = Math.min(this.selected, this.u.worlds.length - 1);
    this.root.classList.remove('hidden');
    this.rebuild();
  }

  close(): void {
    this.save();
    this.root.classList.add('hidden');
  }

  private get world(): WorldSpec {
    return this.u.worlds[this.selected];
  }

  private q<T extends HTMLElement = HTMLElement>(sel: string): T {
    return this.root.querySelector(sel) as T;
  }

  /** Everything again: the list, the panel for the selected world, the previews. */
  private rebuild(): void {
    this.q<HTMLInputElement>('.ce-uname input').value = this.u.name;
    this.renderList();
    this.buildPane();
    this.changed(true);
  }

  /** A few words about a world for the list. */
  private summary(i: number): string {
    const w = this.u.worlds[i];
    const size =
      w.radius >= 1000 ? `${(w.radius / 1000).toFixed(w.radius < 10_000 ? 1 : 0)} km` : `${w.radius} m`;
    const type = PLANET_TYPES[w.type].label.split(':')[0];
    const biomes = w.biomes.map((b) => BIOMES[b.id].label.split(/[:(]/)[0].trim()).join(', ');
    const near = neighbours(this.u, i).map((n) => this.u.worlds[n.world].name);
    const caves = near.length === 0 ? 'no caves' : `caves to ${near.join(', ')}`;
    return `${i === this.u.start ? '★ start · ' : ''}${type} · ${biomes} · radius ${size} · ${caves}`;
  }

  private renderList(): void {
    const box = this.q('.ce-worlds');
    box.replaceChildren(
      ...this.u.worlds.map((w, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `ce-world${i === this.selected ? ' current' : ''}`;
        b.innerHTML = `<b></b><span></span>`;
        b.querySelector('b')!.textContent = w.name;
        b.querySelector('span')!.textContent = this.summary(i);
        b.addEventListener('click', () => this.select(i));
        return b;
      }),
    );
    const add = this.q<HTMLButtonElement>('.ce-add');
    add.disabled = this.u.worlds.length >= MAX_WORLDS;
    add.textContent = add.disabled ? `${MAX_WORLDS} worlds at most` : '+ Add world';
    this.q<HTMLButtonElement>('.ce-remove').disabled = this.u.worlds.length <= 1;
  }

  private select(i: number): void {
    if (i === this.selected) return;
    this.selected = i;
    this.renderList();
    this.buildPane();
    this.schedulePreview();
  }

  private addWorld(): void {
    if (this.u.worlds.length >= MAX_WORLDS) return;
    // Joined to the world being edited: add a few to one and it becomes a hub.
    const { world, cave } = nextWorld(this.u, this.selected);
    this.u.worlds.push(world);
    this.u.caves.push(cave);
    this.selected = this.u.worlds.length - 1;
    this.rebuild();
  }

  /** The settings panel for the selected world. */
  private buildPane(): void {
    this.pane?.dispose();
    this.when.length = 0;
    const w = this.world;
    const i = this.selected;
    const pane = new Pane({ container: this.q('.ce-pane') });
    this.pane = pane;
    const later = (f: () => void) =>
      setTimeout(() => {
        f();
        this.rebuild();
      });
    const num = (
      f: Folder,
      key: keyof typeof RANGES & keyof WorldSpec,
      label: string,
      format?: (v: number) => string,
    ) => f.addBinding(w, key, { label, ...RANGES[key], ...(format ? { format } : {}) });
    const pct = (v: number) => `${Math.round(v * 100)}%`;
    const times = (v: number) => `×${v.toFixed(2)}`;
    const only = (row: Binding | Folder, show: (w: WorldSpec) => boolean) => this.when.push({ row, show });
    const options = <T extends string>(ids: readonly T[], label: (id: T) => string) =>
      Object.fromEntries(ids.map((id) => [label(id), id]));

    // --- The world
    const world = pane.addFolder({ title: 'World' });
    world.addBinding(w, 'name', { label: 'Name' });
    const pick = { preset: '' };
    world
      .addBinding(pick, 'preset', {
        label: 'Preset',
        options: { 'Choose one to start over…': '', ...options(PRESET_IDS, (id) => PRESETS[id].label) },
      })
      .on('change', (ev) => {
        if (!ev.value) return;
        later(() => {
          this.u.worlds[i] = applyPreset(w, ev.value as PresetId);
        });
      });
    const size = {
      get size() {
        return Math.log10(w.radius);
      },
      set size(v: number) {
        const r = 10 ** v;
        const step = r < 2000 ? 10 : r < 20_000 ? 100 : 1000;
        w.radius = Math.round(r / step) * step;
      },
    };
    world.addBinding(size, 'size', {
      label: 'Radius',
      min: Math.log10(RANGES.radius.min),
      max: Math.log10(RANGES.radius.max),
      step: 0.005,
      format: (v: number) => formatDistance(10 ** v),
    });
    world.addBinding(w, 'seed', { label: 'Seed', ...RANGES.seed });
    world.addButton({ title: 'New seed' }).on('click', () => {
      w.seed = Math.floor(Math.random() * 1_000_000);
      pane.refresh();
      this.changed(true);
    });
    num(world, 'gravity', 'Gravity (g)', (v) => (v === 0 ? 'auto' : v.toFixed(2)));
    if (i !== this.u.start) {
      world.addButton({ title: 'Start here' }).on('click', () =>
        later(() => {
          this.u.start = i;
        }),
      );
    }

    // --- The land
    const land = pane.addFolder({ title: 'Land', expanded: false });
    land
      .addBinding(w, 'type', {
        label: 'Planet type',
        options: options(PLANET_TYPE_IDS, (id) => PLANET_TYPES[id].label),
      })
      .on('change', (ev) => later(() => Object.assign(w, typeDefaults(ev.value as PlanetTypeId))));
    land
      .addBinding(w, 'sea', { label: 'Seas', options: { Water: 'water', Lava: 'lava' } })
      .on('change', (ev) =>
        later(() => {
          const d = typeDefaults(ev.value === 'lava' ? 'volcanic' : 'continents');
          w.waterShallow = d.waterShallow!;
          w.waterDeep = d.waterDeep!;
        }),
      );
    num(land, 'landSize', 'Landform size', times);
    num(land, 'relief', 'Height', times);
    num(land, 'water', 'More water', (v) => (v === 0 ? '0' : `${v > 0 ? '+' : '−'}${pct(Math.abs(v))}`));
    num(land, 'mountains', 'Mountains', times);
    num(land, 'hills', 'Hills', times);
    num(land, 'rough', 'Bumpiness', times);
    num(land, 'erosion', 'Erosion');
    only(land.addBinding(w, 'snow', { label: 'Snow on meadows' }), (x) =>
      x.biomes.some((b) => b.id === 'meadow'),
    );
    only(num(land, 'snowLine', 'Snow line (m)'), (x) => x.snow && x.biomes.some((b) => b.id === 'meadow'));

    // --- The biomes
    const biomes = pane.addFolder({
      title: w.biomes.length > 1 ? `Biomes (${w.biomes.length}, mixed)` : 'Biome',
    });
    const biomeOptions = options(BIOME_IDS, (id) => BIOMES[id].label);
    const speciesOptions = options(SPECIES_IDS, (id) => SPECIES[id].label);
    w.biomes.forEach((slot, k) => {
      const f =
        w.biomes.length > 1 ? biomes.addFolder({ title: `${k + 1}. ${BIOMES[slot.id].label}` }) : biomes;
      f.addBinding(slot, 'id', { label: 'Biome', options: biomeOptions }).on('change', (ev) =>
        later(() => {
          w.biomes[k] = biomeSlot(ev.value as BiomeId, slot.share, slot.where);
        }),
      );
      if (w.biomes.length > 1) {
        f.addBinding(slot, 'share', { label: 'Share', min: 0.02, max: 1, step: 0.01, format: pct });
        f.addBinding(slot, 'where', {
          label: 'Prefers',
          options: options(WHERE_IDS, (id) => WHERE_LABELS[id]),
        });
      }
      f.addBinding(slot, 'treeA', { label: 'Trees', options: speciesOptions });
      f.addBinding(slot, 'treeB', { label: 'and', options: speciesOptions });
      if (w.biomes.length > 1) {
        f.addButton({ title: 'Remove this biome' }).on('click', () =>
          later(() => {
            w.biomes.splice(k, 1);
          }),
        );
      }
    });
    if (w.biomes.length < MAX_BIOMES) {
      biomes.addButton({ title: '+ Mix in another biome' }).on('click', () =>
        later(() => {
          const unused = BIOME_IDS.find((id) => !w.biomes.some((b) => b.id === id)) ?? 'meadow';
          w.biomes.push(biomeSlot(unused, 0.3));
        }),
      );
    }
    if (w.biomes.length > 1) {
      num(biomes, 'biomeSize', 'Region size', times);
      num(biomes, 'biomeBlend', 'Borders', (v) => `×${v.toFixed(2)} wide`);
    }

    // --- The sky
    const light = pane.addFolder({ title: 'Sky and light', expanded: false });
    light
      .addBinding(w, 'sky', { label: 'Sky', options: options(SKY_IDS, (id) => SKIES[id].label) })
      .on('change', (ev) => later(() => Object.assign(w, skyDefaults(ev.value as SkyId, w.radius))));
    light.addBinding(w, 'sun', { label: 'Sun' });
    only(light.addBinding(w, 'sunColor', { label: 'Sun colour' }), (x) => x.sun);
    only(num(light, 'sunStrength', 'Sunlight'), (x) => x.sun);
    only(num(light, 'sunSize', 'Sun size', times), (x) => x.sun);
    num(light, 'glow', 'Glow by day');
    num(light, 'glowNight', 'Glow by night');
    only(light.addBinding(w, 'glowColor', { label: 'Glow colour' }), (x) => x.glow > 0);
    num(light, 'daylight', 'Share in daylight', pct);
    num(light, 'dayLength', 'Day length (s)', (v) => v.toFixed(0));
    light.addBinding(w, 'duskColor', { label: 'Dusk colour' });
    num(
      light,
      'startHour',
      'Arrival hour',
      (v) => `${Math.floor(v)}:${String(Math.round((v % 1) * 60)).padStart(2, '0')}`,
    );
    light.addBinding(w, 'lantern', { label: 'Lantern lit' });
    num(light, 'exposure', 'Brightness', times);
    num(light, 'haze', 'Air', (v) => (v === 0 ? 'none' : times(v)));
    only(
      num(light, 'clouds', 'Clouds', (v) => (v === 0 ? 'none' : pct(v))),
      (x) => x.haze > 0,
    );
    only(num(light, 'cloudHeight', 'Cloud height', times), (x) => x.haze > 0 && x.clouds > 0);

    // --- Ground and water (every biome alike)
    const ground = pane.addFolder({ title: 'Ground and water', expanded: false });
    ground.addBinding(w, 'groundTint', { label: 'Tint' });
    num(ground, 'groundBrightness', 'Brightness', times);
    num(ground, 'groundSaturation', 'Saturation', times);
    num(ground, 'groundGlow', 'Glow', times);
    num(ground, 'forests', 'Forests', times);
    num(ground, 'forestSize', 'Forest size', times);
    ground.addBinding(w, 'waterShallow', { label: w.sea === 'lava' ? 'Lava crust' : 'Shallows' });
    ground.addBinding(w, 'waterDeep', { label: w.sea === 'lava' ? 'Deep crust' : 'Deep water' });

    // --- Plants and life (every biome alike)
    const life = pane.addFolder({ title: 'Plants and life', expanded: false });
    num(life, 'grass', 'Grass', (v) => (v === 0 ? 'none' : times(v)));
    life.addBinding(w, 'grassTint', { label: 'Grass tint' });
    num(life, 'trees', 'Tree density', (v) => (v === 0 ? 'none' : times(v)));
    num(life, 'treeSize', 'Tree size', times);
    life.addBinding(w, 'foliageTint', { label: 'Leaf tint' });
    life.addBinding(w, 'rocks', { label: 'Boulders' });
    life.addBinding(w, 'birds', { label: 'Birds' });
    life.addBinding(w, 'motes', {
      label: 'In the air',
      options: options(MOTES_IDS, (id) => MOTES_LABELS[id]),
    });

    // --- Its caves: the rock between it and each world it's joined to
    const near = neighbours(this.u, i);
    if (near.length > 0) {
      const caves = pane.addFolder({ title: `Caves (${near.length})` });
      for (const n of near) {
        const cave = this.u.caves[n.cave];
        const proxy = {
          get length() {
            return cave.length;
          },
          set length(v: number) {
            cave.length = v <= 0 ? 0 : Math.max(v, MIN_GAP);
          },
        };
        caves.addBinding(proxy, 'length', {
          label: `Rock to ${this.u.worlds[n.world].name}`,
          ...CAVE_LENGTH,
          format: (v: number) => (v === 0 ? 'automatic' : formatDistance(Math.max(v, MIN_GAP))),
        });
      }
    }

    pane.on('change', (ev) => {
      const key = (ev.target as unknown as { key?: string }).key;
      if (key === 'preset' || key === 'type' || key === 'sky' || key === 'id' || key === 'sea') return;
      this.showRows();
      this.changed(ev.last !== false);
    });
    this.showRows();
  }

  private showRows(): void {
    const w = this.world;
    for (const r of this.when) r.row.hidden = !r.show(w);
  }

  /** Something changed: save soon; list labels now; previews once the change settles. */
  private changed(settled: boolean): void {
    clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.save(), 400);
    const buttons = this.root.querySelectorAll('.ce-world');
    if (buttons.length !== this.u.worlds.length) this.renderList();
    else {
      buttons.forEach((b, i) => {
        b.querySelector('b')!.textContent = this.u.worlds[i].name;
        b.querySelector('span')!.textContent = this.summary(i);
      });
    }
    this.q('.ce-map-name').textContent = this.world.name;
    if (settled) this.schedulePreview();
  }

  private save(): void {
    clearTimeout(this.saveTimer);
    saveUniverse(this.u);
  }

  private schedulePreview(): void {
    clearTimeout(this.previewTimer);
    this.previewTimer = window.setTimeout(() => void this.preview(), 250);
  }

  /** Place the universe (as the game will) and draw both previews. */
  private async preview(): Promise<void> {
    const epoch = ++this.previewEpoch;
    const notes = this.q('.ce-notes');
    try {
      this.built = buildUniverse(this.u);
    } catch (e) {
      notes.textContent = `Can't place these worlds: ${e instanceof Error ? e.message : String(e)}`;
      return;
    }
    const built = this.built;
    this.drawn = drawUniverse(this.q<HTMLCanvasElement>('.ce-universe'), this.u, built, this.selected);
    const hubs = this.u.worlds
      .map((w, i) => ({ w, n: neighbours(this.u, i).length }))
      .filter((x) => x.n >= 3)
      .map((x) => `${x.w.name} is a hub (${x.n} caves).`);
    notes.replaceChildren(
      ...[...hubs, ...built.notes].map((t) => {
        const p = document.createElement('div');
        p.textContent = t;
        return p;
      }),
    );

    const i = this.selected;
    const w = this.world;
    this.q('.ce-map-name').textContent = w.name;
    const marks: Vector3[] = [];
    for (const l of built.placement.links) {
      if (l.from === i) marks.push(l.mouths[0]);
      if (l.to === i) marks.push(l.mouths[1]);
    }
    if (this.u.worlds.length === 1) marks.push(built.placement.spawn.dir);
    const stats = await drawWorldMap(
      this.q<HTMLCanvasElement>('.ce-map'),
      w,
      built.terrains[i],
      built.fields[i],
      marks,
      () => epoch !== this.previewEpoch,
    );
    if (!stats || epoch !== this.previewEpoch) return;
    const g = w.gravity > 0 ? w.gravity : defaultSurfaceGravity(w.radius) / G0;
    const biomes =
      w.biomes.length > 1
        ? ` · ${w.biomes.map((b, k) => `${BIOMES[b.id].label.split(/[:(]/)[0].trim()} ${Math.round(stats.biomes[k] * 100)}%`).join(', ')}`
        : '';
    this.q('.ce-stats').textContent =
      `${formatDistance(2 * w.radius)} across · gravity ${g.toFixed(2)} g · ${Math.round(stats.water * 100)}% ${w.sea === 'lava' ? 'lava' : 'water'} · highest ground ${formatDistance(Math.max(0, stats.peak))}${biomes}`;
  }

  /** The prefab list: choosing one replaces the universe being edited (after asking). */
  private wirePrefabs(): void {
    const select = this.q<HTMLSelectElement>('.ce-prefab');
    select.append(
      new Option('Start from a prefab universe…', ''),
      ...PREFAB_IDS.map((id) => {
        const p = PREFABS[id];
        const o = new Option(`${p.label} (${p.make().worlds.length} worlds)`, id);
        o.title = p.about;
        return o;
      }),
    );
    select.addEventListener('change', () => {
      const id = select.value as PrefabId | '';
      select.value = '';
      if (!id) return;
      const p = PREFABS[id];
      const n = this.u.worlds.length;
      const ask = `Replace "${this.u.name}" (${n} world${n === 1 ? '' : 's'}) with "${p.label.split(':')[0]}"? Copy a share link first to keep it.`;
      if (!window.confirm(ask)) return;
      this.u = p.make();
      this.selected = this.u.start;
      this.q('.ce-status').textContent = p.about;
      this.rebuild();
    });
  }

  /** The universe view: click a world to edit it, drag it onto another to join the two by a cave. */
  private wireUniverseView(): void {
    const canvas = this.q<HTMLCanvasElement>('.ce-universe');
    const point = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * canvas.width,
        y: ((e.clientY - rect.top) / rect.height) * canvas.height,
      };
    };
    const hit = (x: number, y: number) => {
      let best = -1;
      let bestD = Number.POSITIVE_INFINITY;
      this.drawn.forEach((d, i) => {
        const dist = Math.hypot(x - d.x, y - d.y) - d.r;
        if (dist < 8 && dist < bestD) {
          best = i;
          bestD = dist;
        }
      });
      return best;
    };
    const redraw = () => {
      if (this.built) drawUniverse(canvas, this.u, this.built, this.selected, this.drag, this.drawn);
    };
    canvas.addEventListener('pointerdown', (e) => {
      const at = point(e);
      const i = hit(at.x, at.y);
      if (i < 0) return;
      this.drag = { from: i, x: at.x, y: at.y, over: -1 };
      this.dragMoved = false;
      canvas.setPointerCapture(e.pointerId);
    });
    canvas.addEventListener('pointermove', (e) => {
      const at = point(e);
      if (!this.drag) {
        canvas.style.cursor = hit(at.x, at.y) >= 0 ? 'grab' : 'default';
        return;
      }
      if (Math.hypot(at.x - this.drag.x, at.y - this.drag.y) > 4) this.dragMoved = true;
      this.drag.x = at.x;
      this.drag.y = at.y;
      this.drag.over = hit(at.x, at.y);
      canvas.style.cursor = 'grabbing';
      if (this.dragMoved) redraw();
    });
    const end = () => {
      const d = this.drag;
      this.drag = null;
      canvas.style.cursor = 'default';
      if (!d) return;
      if (!this.dragMoved) {
        this.select(d.from);
        redraw();
        return;
      }
      if (d.over >= 0 && d.over !== d.from) {
        const before = this.u;
        this.u = connect(this.u, d.from, d.over);
        const [a, b] = [this.u.worlds[d.from].name, this.u.worlds[d.over].name];
        this.q('.ce-status').textContent =
          this.u === before ? `${a} and ${b} are already joined.` : `${a} now has a cave to ${b}.`;
        this.selected = d.from;
        this.rebuild();
        return;
      }
      redraw();
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
  }

  private shareUrl(): string {
    const url = new URL(location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('scene', 'custom');
    url.searchParams.set('w', encodeUniverse(this.u));
    return url.toString();
  }

  private async share(): Promise<void> {
    const status = this.q('.ce-status');
    const link = this.shareUrl();
    try {
      await navigator.clipboard.writeText(link);
      status.textContent = 'Link copied.';
    } catch {
      window.prompt('Copy this link:', link);
    }
  }

  /** Save and load the universe (the page reloads into it). */
  private play(): void {
    this.save();
    const url = new URL(location.href);
    url.searchParams.set('scene', 'custom');
    url.searchParams.delete('w');
    url.searchParams.delete('cam');
    location.href = url.toString();
  }
}
